import React, { useCallback, useMemo, useEffect } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import {
  useGetMyAccountQuery,
  BasicEventDetailsFragment,
  useGetUpcomingEventsQuery,
  useGetAllEventsLazyQuery,
  GetAllEventsQueryVariables,
} from 'graphql/types';
import { PaginatingItems, Button, Container, SearchSidebar, SearchToolbarWrapper } from 'components/elements';
import EventCard from 'components/eventCard';
import { useHistory, useLocation } from 'react-router-dom';
import { notification } from 'antd';
import qs from 'query-string';
import {
  PageHeader,
  PageHeaderRow,
  PageHeaderAction,
  PageTitle,
  PageShell,
  MyGroupsSections,
} from 'containers/groupsView/groupsView.styled';
import { EventListWrapper } from './events.styled';

const allEventsPageSize = 8;

type MyBookingEvents = BasicEventDetailsFragment[];
const Events = () => {
  const { data, loading } = useGetMyAccountQuery();
  const { data: upcomingEvents, loading: upcomingEventsLoading } = useGetUpcomingEventsQuery();
  const history = useHistory();

  const [getAllEvents, { data: allEventsResult, loading: allEventsLoading }] = useGetAllEventsLazyQuery();

  const { search } = useLocation();
  const searchQuery = useMemo(() => qs.parse(search, { arrayFormat: 'bracket' }), [search]);

  const updatePageParams = (searchParams: any) => {
    history.push(`/events?${qs.stringify(searchParams, { arrayFormat: 'bracket' })}`);
  };

  const getAllEventsPage = useCallback(
    (page: number) => {
      const searchFilter = {
        sort: 'createdAt:desc',
        pagination: { page, pageSize: allEventsPageSize },
      } as GetAllEventsQueryVariables;
      const searchFilterProps = {} as any;
      if (searchQuery?.search) {
        const rawSearch = String(searchQuery.search).trim();
        const normalizedSearch = rawSearch.normalize('NFD').replace(/[̀-ͯ]/g, '');
        const orFilters = [{ title: { containsi: rawSearch } }, { description: { containsi: rawSearch } }];
        if (normalizedSearch && normalizedSearch !== rawSearch) {
          orFilters.push({ title: { containsi: normalizedSearch } });
          orFilters.push({ description: { containsi: normalizedSearch } });
        }
        searchFilterProps.or = orFilters;
      }

      getAllEvents({ variables: { ...searchFilter, filters: searchFilterProps } });
    },
    [getAllEvents, searchQuery],
  );

  useEffect(() => {
    getAllEventsPage(1);
  }, [getAllEvents, getAllEventsPage, searchQuery]);

  const allEventsData = allEventsResult?.events?.data || [];
  const allEventsPagination = allEventsResult?.events?.meta?.pagination;

  const onAddNewEvent = useCallback(() => {
    if (data?.myAccount?.data?.attributes?.ledGroups && data?.myAccount?.data?.attributes?.ledGroups?.data.length) {
      history.push('/events/create');
    } else {
      notification.warning({
        message: "You Don't have any Group",
        description: 'In order to make your first Event You need to create your Group first.',
      });
    }
  }, [data, history]);

  const myBookings = useMemo(() => {
    const unwrap = (collection: any) =>
      (collection?.data ?? []).map((item: any) => ({ id: item.id, ...item.attributes }));

    const events = unwrap(data?.myAccount?.data?.attributes?.events);
    // const joinedEvents = unwrap(data?.myAccount?.data?.attributes?.joinedEvents);

    const combined = Array.from(new Map([...events].map((event: any) => [event.id, event])).values());

    const toTime = (v: any) => (v ? new Date(v as any).getTime() : Number.POSITIVE_INFINITY);

    combined.sort((a, b) => toTime(a?.startTime) - toTime(b?.startTime));

    return combined.reduce(
      (acc, booking) => {
        const eventEnity = {
          ...booking,
        };
        if (eventEnity.id) {
          if (booking?.isPast) {
            acc.past.push(eventEnity);
          } else {
            acc.upcoming.push(eventEnity);
          }
        }
        return acc;
      },
      { past: [] as MyBookingEvents, upcoming: [] as MyBookingEvents },
    );
  }, [data]);

  const addEventButton = (
    <PageHeaderAction>
      <Button type="primary" onClick={onAddNewEvent}>
        <PlusOutlined />
        Create New Event
      </Button>
    </PageHeaderAction>
  );

  const hasSearchKeyword = Boolean((searchQuery.search as string)?.trim());

  const myEventsSections = (
    <MyGroupsSections>
      <section>
        <PaginatingItems
          title="My Upcoming Events"
          items={upcomingEvents?.upcomingEvents || []}
          renderList={(props) => <EventCard {...props} id={props.id} bookings={props?.bookings?.data} />}
          loading={upcomingEventsLoading}
          perView={4}
          pageSize={8}
          controlledPaginating
        />
      </section>
      <section>
        <PaginatingItems
          title="My Past Events"
          items={myBookings.past}
          renderList={EventCard}
          loading={loading}
          perView={4}
          pageSize={8}
          controlledPaginating
        />
      </section>
    </MyGroupsSections>
  );

  const allEventsSection = (
    <PaginatingItems
      title={hasSearchKeyword ? 'Search Results' : 'All Events'}
      items={allEventsData}
      loading={allEventsLoading}
      renderList={(props) => <EventCard {...props?.attributes} id={props.id} />}
      perView={4}
      pageSize={allEventsPagination?.pageSize}
      total={allEventsPagination?.total}
      current={allEventsPagination?.page}
      onChange={(page) => getAllEventsPage(page)}
    />
  );

  return (
    <Container>
      <PageShell>
        <PageHeader>
          <PageHeaderRow>
            <PageTitle>Explore Events</PageTitle>
            {addEventButton}
          </PageHeaderRow>
        </PageHeader>
        <SearchToolbarWrapper>
          <SearchSidebar
            searchDefaultValue={(searchQuery.search as string) || ''}
            onSearch={(value) => updatePageParams({ ...searchQuery, search: value })}
          />
        </SearchToolbarWrapper>

        <EventListWrapper>
          {hasSearchKeyword ? (
            <>
              {allEventsSection}
              {myEventsSections}
            </>
          ) : (
            <>
              {myEventsSections}
              {allEventsSection}
            </>
          )}
        </EventListWrapper>
      </PageShell>
    </Container>
  );
};

export default Events;
